// src/components/PhotoGallery/PhotoGallery.tsx
import React from 'react'
import { Badge, Button, Modal, Spinner } from 'react-bootstrap'
import { loadPhotos as fetchPhotos } from '../../_services/photo.service'

interface PhotoRecord {
  id:         string
  name:       string
  url?:       string   // new format (Storage URL)
  image?:     string   // old format (base64)
  base64?:    string   // legacy
  size?:      number
  type?:      string
  sort?:      number
  createdAt?: number
}

interface Props {
  customerId: string
  photoCount?: number
}

function formatSize(bytes?: number) {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function toDataUrl(p: PhotoRecord): string {
  if (p.url) return p.url
  const b64  = p.image || p.base64 || ''
  const mime = p.type || 'image/jpeg'
  if (!b64) return ''
  if (b64.startsWith('data:')) return b64
  return `data:${mime};base64,${b64}`
}

export default function PhotoGallery({ customerId, photoCount }: Props) {
  const [show,    setShow]    = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [photos,  setPhotos]  = React.useState<PhotoRecord[]>([])
  const [preview, setPreview] = React.useState<PhotoRecord | null>(null)
  const [loaded,  setLoaded]  = React.useState(false)

  const handleOpen = async () => {
    if (loaded) { setShow(true); return }
    setShow(true)
    setLoading(true)
    try {
      const list = await fetchPhotos(customerId)
      setPhotos(list as PhotoRecord[])
      setLoaded(true)
    } catch (e) {
      console.error('load photos error', e)
    } finally {
      setLoading(false)
    }
  }

  const count = loaded ? photos.length : (photoCount ?? 0)

  return (
    <>
      <Button variant='outline-secondary' size='sm' onClick={handleOpen} style={{ fontSize: 12 }}>
        <i className='fa-solid fa-images me-1' />รูปภาพ
        {count > 0 && (
          <Badge bg='warning' text='dark' className='ms-1' style={{ fontSize: 10 }}>{count}</Badge>
        )}
      </Button>

      {/* Gallery Modal */}
      <Modal show={show} onHide={() => setShow(false)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>
            <i className='fa-solid fa-images me-2' />รูปภาพประกอบ
            {photos.length > 0 && (
              <Badge bg='secondary' className='ms-2' style={{ fontSize: 11 }}>{photos.length} รูป</Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 200 }}>
              <Spinner animation='border' variant='warning' />
              <span className='ms-2 text-muted' style={{ fontSize: 13 }}>กำลังโหลดรูปภาพ...</span>
            </div>
          )}
          {!loading && photos.length === 0 && (
            <div className='text-center text-muted py-5' style={{ fontSize: 13 }}>
              <i className='fa-solid fa-image' style={{ fontSize: 32, opacity: 0.3, display: 'block', marginBottom: 8 }} />
              ไม่มีรูปภาพ
            </div>
          )}
          {!loading && photos.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
              {photos.map((p, i) => {
                const src = toDataUrl(p)
                return (
                  <div key={p.id || i} onClick={() => setPreview(p)}
                    style={{ cursor: 'pointer', borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e0e0', transition: 'transform 0.15s' }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  >
                    {src
                      ? <img src={src} alt={p.name} loading='lazy' style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
                      : <div style={{ height: 120, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className='fa-solid fa-image' style={{ color: '#ccc', fontSize: 24 }} />
                        </div>
                    }
                    <div style={{ padding: '4px 6px', backgroundColor: '#fafafa' }}>
                      <div style={{ fontSize: 10, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                      {p.size && <div style={{ fontSize: 9, color: '#aaa' }}>{formatSize(p.size)}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Preview full size */}
      <Modal show={!!preview} onHide={() => setPreview(null)} centered size='xl'>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 14 }}>
            {preview?.name}
            {preview?.size && <span className='text-muted ms-2' style={{ fontSize: 11 }}>({formatSize(preview.size)})</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 8, textAlign: 'center', backgroundColor: '#111' }}>
          {preview && <img src={toDataUrl(preview)} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />}
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: 'space-between' }}>
          <div className='d-flex gap-2'>
            <Button size='sm' variant='outline-secondary'
              disabled={photos.findIndex((p) => p.id === preview?.id) <= 0}
              onClick={() => { const i = photos.findIndex((p) => p.id === preview?.id); if (i > 0) setPreview(photos[i - 1]) }}
            ><i className='fa-solid fa-arrow-left me-1' />ก่อนหน้า</Button>
            <Button size='sm' variant='outline-secondary'
              disabled={photos.findIndex((p) => p.id === preview?.id) >= photos.length - 1}
              onClick={() => { const i = photos.findIndex((p) => p.id === preview?.id); if (i < photos.length - 1) setPreview(photos[i + 1]) }}
            >ถัดไป<i className='fa-solid fa-arrow-right ms-1' /></Button>
          </div>
          <div style={{ fontSize: 12, color: '#888' }}>
            {photos.findIndex((p) => p.id === preview?.id) + 1} / {photos.length}
          </div>
          <Button size='sm' variant='outline-secondary' onClick={() => setPreview(null)}>ปิด</Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}